/**
 * **右键菜单 + 菜单栏 + 菜单分发** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 菜单做成**数据**（模型见 `types/menu.ts`）而不是直接写模板，这里是
 * 「把模型拼出来」和「把事件分发出去」两半：
 *
 *   `contextSections`  右键：对当前选择 / 连线 / 节点的操作
 *   `barSections`      菜单栏（文件 / 编辑 / 视图）
 *   `menuSections`     真正喂给 `NpMenu` 的那一份（按 `menu.kind` 分派）
 *   `onMenuSelect` / `closeMenu`      选中与关闭（后者还管 link 菜单的「断开」）
 *   `onNodeMenu` / `projectOntoEdge`  右键落在节点 / 连线上的入口
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `menu` / `contextEdgeId` / `menuPicked` 由宿主注入 —— 前两个声明在
 * `useAddNode` **之前**（那个 composable 的 `run` 里要读菜单落点），
 * `menuPicked` 被更早的 `runGestureCommands` 写，所以三个都必须留在宿主。
 */
import { computed, nextTick, ref } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge, FlowNode as Node } from '@/graph/flowTypes'
import { boundsFor, frameFor, moveMembers } from '@/graph/frame'
import { FLOW_DOM } from '@/graph/graphDom'
import { ASSETS } from '@/playground/nodeTypes'
import { areaSections } from '../menus/areaSections'
import { makeContextSections } from '../menus/contextSections'
import { MENUS, makeBarSections } from '../menus/barSections'
import type { BarMenuId } from '../menus/barSections'
import { pruneMenu } from '@/types/menu'
import type { MenuItem, MenuSection } from '@/types/menu'
import type { ThemeSummary } from '@/types/theme'
import { MIN_NODE_WIDTH } from '@/theme/tokens'
import type { EdgeRef, HandleRef } from '@/graph/canvasGesture'
import type { CutCandidate, Point } from '@/graph/cutGeometry'
import type { FlowStoreApi } from '@/graph/flowStore'
import type { GraphNodeModel } from '@/types/node'
import { ARRANGE_SIZE_MODES, arrangeRows } from '@/graph/arrangeTools'
import { EDITOR_META, EDITOR_ORDER, REGION_LABELS, areasOf, editorOf, findArea } from '@/layout/areas'
import type { AreaLeaf, EditorType, LayoutState, RegionKey } from '@/layout/areas'
import type { AlignMode, SizeMode } from '@/graph/nodeAlign'
import type { NoticeActionInput } from './useUndoRedo'
import { WINDOW_LABELS } from './useAreas'
import type { WindowKey, WindowsView } from './useAreas'
import type { InstalledPackageView } from './usePackages'
import type { PackageExample } from '@/node/package'
import type { WorkspaceEntry } from '@/layout/workspaces'

/** 菜单有三种 + 菜单栏，共用同一个组件 */
export type MenuKind =
  | 'context'
  | 'add'
  | 'link'
  | 'bar'
  | 'area'
  | 'asset'
  /**
   * **窗口类型菜单** —— 点窗口 header 左上角那个图标弹出来的。
   *
   * ⚠️ 它和 `area` 是**两套**菜单，不是同一套的两个入口：
   * 左上角图标回答「这个窗口显示什么」，右边「⋯」回答
   * 「这个窗口怎么摆（分屏 / 最大化 / 关闭）」。
   * 原来两者共用一个菜单，于是点图标要先看到一堆结构操作才能找到窗口类型。
   */
  | 'editor'

/**
 * 拖线松手落空那次菜单的上下文。
 *
 * ⚠️ 本地定义，不从 Playground 导 —— composable 不该反向依赖宿主文件。
 */
export interface LinkMenuContext {
  /** 这次要接的起点端口（从输入拖出来时是**原输出端口**） */
  origin: HandleRef
  /** 松手的屏幕坐标 —— 新建的节点就落在这里 */
  point: { x: number; y: number }
  /** 从已有输入拖出来时带的原边：菜单被关掉（没选东西）就是「断开」 */
  undoEdge?: EdgeRef
}

export interface MenuState {
  kind: MenuKind
  x: number
  y: number
  /** `kind === 'area'` 时：右键的是哪一个 Area（见 `areaSections`） */
  areaId?: string
  link?: LinkMenuContext
}

export interface MenusCtx {
  /** 菜单状态（宿主提前声明：`useAddNode` 的 run 里要读落点） */
  menu: { value: MenuState | null }
  /** 右键落在某条连线上时的边 id */
  contextEdgeId: { value: string | null }
  /** 区分「选了某一项」和「直接关掉」（更早的手势代码也写它） */
  /** 宿主里的 ref（与 runGestureCommands 共用一个盒子） */
  menuPicked: { value: boolean }

  nodes: { value: Node[] }
  edges: { value: Edge[] }
  flowStore: { value: FlowStoreApi | null }
  graphRef: { value: HTMLElement | null }
  activeNodeId: { value: string | null }
  selectedIds: { value: string[] }
  edgeViews: () => EdgeViewLike[]
  currentModels: () => GraphNodeModel[]
  modelOf: (id: string) => GraphNodeModel | undefined
  /** 密集采样版 —— 双击线条时要让接通节点落在**光标附近**，见 useCutLasso */
  sampleEdgePathFine: (path: SVGPathElement) => Point[]
  /** 打开「添加节点」菜单（来自 useAddNode） */
  openAddMenu: (point?: { x: number; y: number }) => void
  /** 当前选中的 id 集合（右键节点时判断「是不是在选中里」） */
  selectedIdSet: { value: Set<string> }
  /**
   * 组件 props（宿主是外部可定制的那个壳）。
   *
   * ⚠️ 宿主传进来的是**reactive 对象**（里面有当前文档的名字与 UUID）——
   * 菜单里那两行要跟着文档库改名而变，快照不行。
   */
  props: { documentId?: string; documentName?: string }
  /** ★ 文档库那几项（2027 加）—— 见 `BarMenuCtx` 里的说明 */
  documentDirty: { value: boolean }
  openDocumentsWindow: () => void
  saveDocument: () => void

  /* ── 段落之间的依赖（宿主里的 computed / 函数） ── */
  addSections: { value: MenuSection[] }
  linkCandidateSections: (ctx: LinkMenuContext) => MenuSection[]
  toFlowPoint: (p: { x: number; y: number }) => { x: number; y: number }
  insertRerouteOnEdge: (
    edge: {
      id?: string
      source: string
      sourceHandle?: string | null
      target: string
      targetHandle?: string | null
    },
    at: Point | null,
    ev?: MouseEvent | TouchEvent,
  ) => void
  putEdgeBack: (edge: EdgeRef) => void

  /* ── 操作 ── */
  historyState: { value: { canUndo: boolean; canRedo: boolean; undo?: string; redo?: string } }
  clipboardState: { value: { canCopy: boolean; canPaste: boolean } }
  /** 窗口开关的**只读**视图（从布局树派生，见 useAreas） */
  windows: { value: WindowsView }
  /** 唯一的写入口 —— 「输出窗口」那一项是增删 Area，不是翻布尔 */
  toggleWindow: (key: WindowKey) => void
  /** 全部工作区（自存的 + 内置预设），顺序就是标签栏/菜单里的顺序 */
  workspaces: { value: WorkspaceEntry[] }
  /** 当前处在哪个工作区（null = 结构不对应任何一个） */
  activeWorkspaceId: { value: string | null }
  /** 套用一份工作区 */
  applyWorkspace: (entry: WorkspaceEntry) => void
  /** 把当前排布存成一个命名工作区，返回新 id */
  saveWorkspace: (label: string) => string
  /** 删掉一个自存的工作区 */
  deleteWorkspace: (id: string) => void
  /** 布局树（Area 菜单要读「还有哪些 Area」「是不是最大化了」） */
  layout: { value: LayoutState }
  /** 交换两个 Area 的内容 */
  swapAreas: (a: string, b: string) => void
  /** 最大化 / 还原某个 Area */
  toggleMaximize: (areaId: string) => void
  /** 关掉某个 Area */
  closeArea: (areaId: string) => void
  /** 切换某个 Area 的 Region 开关（「⋯」菜单要用） */
  toggleRegionOf: (areaId: string, key: RegionKey) => void
  /** 给某个 Area 分屏（「⋯」菜单要用） */
  splitAreaOf: (areaId: string, dir: 'row' | 'col') => void
  /** 换某个 Area 的编辑器类型（header 左上角那个按钮的菜单用它） */
  setEditorOf: (areaId: string, editor: EditorType) => void
  /**
   * 设某个 Area 某一侧的侧栏（`null` = 收起）。
   *
   * ⚠️ 面板开合归**布局**（`AreaLeaf.panels`）：header 上的按钮和「⋯」菜单里
   * 的开关读写的是同一份，所以这里必须有一条宿主的写入口。
   */
  setPanelOfSide: (areaId: string, side: 'left' | 'right', tab: string | null) => void
  /** 「系统 → 关于」 */
  openAbout: () => void
  /** 「系统 → 帮助」 */
  openHelp: () => void
  /** 切换主题（主题注册表的 id） */
  setTheme: (id: string) => void
  /** 全部可选主题（内置 + 用户）—— 菜单子项**从它生成** */
  themes: { value: ThemeSummary[] }
  /** 当前主题 id */
  themeId: { value: string }
  /** 当前主题摘要（菜单项副标题用） */
  themeSummary?: ThemeSummary
  /** 打开主题面板 */
  toggleThemeWindow: () => void
  commit: (label: string, coalesceMs?: number) => void
  doUndo: () => void
  doRedo: () => void
  doCopy: () => void
  doPaste: () => void
  doAlign: (mode: AlignMode) => void
  /** 等宽 / 等高 */
  doResize: (mode: SizeMode) => void
  /** 「能不能等宽/等高」的纯判定，和 `canAlign` 对称 */
  canResize: (count: number, mode: SizeMode) => boolean
  /**
   * 通知**所有**节点图视图重算端口位置。
   *
   * ⚠️ 菜单里改宽度之后必须重算，而重算要发给**每一个**窗口
   * （另一个窗口不重算的话连线端点会停在旧几何上）——
   * 所以注入的是宿主那个「发全部」的版本，不是 `flowStore.value?.updateNodeInternals`。
   */
  updateNodeInternals: (nodeIds?: string[]) => void
  /** 剪切 = 复制 + 删除（宿主组合出来的，没有独立实现） */
  doCut: () => void
  canAlign: (count: number, mode: AlignMode) => boolean
  toggleHideUnused: () => void
  deleteSelectedNodes: (mode?: 'frame-only' | 'with-children') => void
  selectAll: () => void
  groupSelection: () => void
  ungroupSelection: () => void
  wrapSelectionInFrame: () => void
  closePanels: () => void
  exportRenderTree: () => void
  /** 导出**完整**文档（含 design 字段），给「文件 → 导出设计文档」用 */
  exportDocument: () => void
  /** 保存整份模板文件（设计 + 工具设置），下次可直接打开 */
  saveTemplateFile: () => void
  /** 打开一份模板文件（会先确认再替换当前画布） */
  /** 已安装的节点包（含「能不能卸载」与启用状态） */
  installedPackages: { value: InstalledPackageView[] }
  /** 给组的内部接口节点加一个固定参数 */
  /** 「添加资产」菜单的分组（组节点资产） */
  assetSections: { value: MenuSection[] }
  /** 打开「添加资产」菜单 */
  openAssetMenu: (point?: { x: number; y: number }) => void
  /** 弹文件框导入一个节点包 */
  importPackage: () => void
  /** 卸载一个节点包（画布上已有的节点不受影响） */
  uninstallPackage: (id: string) => void
  /** 重新启用一个被卸载的内置包 */
  enablePackage: (id: string) => void
  /** 导出某个包（给人当模板改） */
  exportPackage: (id: string) => void
  /** 打开 / 导入一份 .json（进库 + 打开；UUID 撞车由宿主弹窗裁决） */
  openDocumentFile: () => void
  /** ★ 包带来的**示例文档**（「文件 → 示例文件」子菜单用它） */
  examples: { value: PackageExample[] }
  /** 打开一份示例（宿主负责把"模型"变成"文档"再走导入那条路） */
  openExample: (id: string) => void
  showNotice: (message: string, actions?: NoticeActionInput | NoticeActionInput[]) => void
  resetGraph: () => void
}

/** 边视图 —— 只描述这个文件用得到的形状 */
export interface EdgeViewLike {
  id?: string
  muted?: boolean
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}

export function useMenus(ctx: MenusCtx) {
  const {
    menu,
    contextEdgeId,
    nodes,
    edges,
    flowStore,
    graphRef,
    activeNodeId,
    selectedIds,
    edgeViews,
    currentModels,
    modelOf,
    sampleEdgePathFine,
    openAddMenu,
    selectedIdSet,
    props,
    /* ★ 文档库（2027 加）—— 菜单栏「文件」那一档要读它们 */
    documentDirty,
    openDocumentsWindow,
    saveDocument,
    openDocumentFile,
    /* ★ 示例文档（包带的）—— 「文件 → 示例文件」那一栏 */
    examples,
    openExample,
    addSections,
    linkCandidateSections,
    toFlowPoint,
    insertRerouteOnEdge,
    putEdgeBack,
    historyState,
    clipboardState,
    windows,
    toggleWindow,
    workspaces,
    activeWorkspaceId,
    applyWorkspace,
    saveWorkspace,
    deleteWorkspace,
    layout,
    swapAreas,
    toggleMaximize,
    closeArea,
    toggleRegionOf,
    splitAreaOf,
    setTheme,
    themes,
    themeId,
    themeSummary,
    toggleThemeWindow,
    commit,
    doUndo,
    doRedo,
    doCopy,
    doPaste,
    doAlign,
    doResize,
    doCut,
    updateNodeInternals,
    canAlign,
    canResize,
    toggleHideUnused,
    deleteSelectedNodes,
    selectAll,
    groupSelection,
    ungroupSelection,
    wrapSelectionInFrame,
    closePanels,
    exportRenderTree,
    exportDocument,
    saveTemplateFile,
    installedPackages,
  assetSections,
  openAssetMenu,
  importPackage,
  uninstallPackage,
  enablePackage,
  exportPackage,
    showNotice,
    resetGraph,
    openAbout,
    openHelp,
  } = ctx

/*
 * 菜单图标。
 *
 * ⚠️ 统一 24×24 viewBox + `stroke="currentColor"`：
 * `NpMenu` 用 `v-html` 塞进来，颜色靠 CSS 的 `color` 继承 ——
 * 这样禁用态（灰）、危险态（红）不用各写一套图标。
 *
 * 「排列」那三组的图标在 `graph/arrangeTools.ts` —— 它们和
 * 「有哪些对齐方式」是同一份产品定义，放在一起才不会漏。
 */
const ICONS = {
  undo: '<path d="M9 14 4 9l5-5"/><path d="M4 9h10a6 6 0 0 1 0 12h-3"/>',
  redo: '<path d="m15 14 5-5-5-5"/><path d="M20 9H10a6 6 0 0 0 0 12h3"/>',
  copy: '<rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/>',
  paste: '<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/>',
  cut: '<circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12"/>',
  trash: '<path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/><path d="M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6"/><path d="M10 11v6M14 11v6"/>',
} as const

/*
 * ★ 右键菜单那 330 行**已向下拆到** `menus/contextSections.ts`。
 * 它只用得到本文件 67 个字段里的一部分，和顶部菜单栏那套**交集为 0**。
 *
 * ⚠️ `run: doUndo` 这种**引用传递**保持原样（不包箭头函数）——
 * 见子模块开头的说明。
 */
const contextSections = makeContextSections({
  menu,
  contextEdgeId,
  selectedIds,
  /*
   * ⚠️ `edgeViews` 转一手：宿主那份返回的视图类型比子模块要的更宽
   * （多几个可选字段）。这里断言成子模块声明的形状 ——
   * 它读的字段（`id` / `muted` / 两端 handle）宿主那份都有。
   */
  edgeViews: () => edgeViews() as unknown as Parameters<typeof makeContextSections>[0] extends {
    edgeViews: () => infer R
  }
    ? R
    : never,
  edges,
  edgeMuted,
  modelOf,
  historyState,
  clipboardState,
  /*
   * ⚠️ 传 **getter**，不是 `.value` —— 这两个是 computed（装卸包会变），
   * 传值等于冻住那一刻的清单。`menuSections` 那条路径读的是活的，
   * 所以传值的后果是「右键菜单和 Shift+A 看到的资产不一样」。
   */
  addSections: () => addSections.value,
  assetSections: () => assetSections.value,
  openAddMenu,
  openAssetMenu,
  toFlowPoint,
  insertRerouteOnEdge,
  doUndo,
  doRedo,
  doCopy,
  doPaste,
  doCut,
  doAlign,
  doResize,
  canAlign,
  canResize,
  commit,
  updateNodeInternals,
  toggleHideUnused,
  deleteSelectedNodes,
  groupSelection,
  ungroupSelection,
  wrapSelectionInFrame,
})



function openBarMenu(ev: MouseEvent, id: string) {
  const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect()
  /*
   * ⚠️ 参数收 `string` 而不是 `BarMenuId`：顶栏抽成子组件后，
   * 它的 emit 只能声明 `string`（要兼容宿主这个更窄的联合类型，
   * 参数类型必须更宽）。这里再收窄回 `BarMenuId`。
   */
  barMenuId.value = id as BarMenuId
  menu.value = { kind: 'bar', x: rect.left, y: rect.bottom + 4 }
}

/*
 * ★ 顶部菜单栏那 300 行**已向下拆到** `menus/barSections.ts`，
 * `MENUS` 与 `barMenuId` 也跟着走了（它们只服务这一套）。
 *
 * ⚠️ `run: doUndo` 这种**引用传递**保持原样（不包箭头函数）。
 */
const { barSections, barMenuId } = makeBarSections({
  props,
  documentDirty,
  openDocumentsWindow,
  saveDocument,
  openDocumentFile,
  examples,
  openExample,
  openAbout,
  openHelp,
  setTheme,
  themes,
  themeId,
  themeSummary,
  toggleThemeWindow,
  flowStore,
  selectedIds,
  historyState,
  windows,
  layout,
  installedPackages,
  workspaces,
  activeWorkspaceId,
  toggleWindow,
  applyWorkspace,
  saveWorkspace,
  deleteWorkspace,
  importPackage,
  uninstallPackage,
  enablePackage,
  exportPackage,
  saveTemplateFile,
  exportDocument,
  exportRenderTree,
  resetGraph,
  openAddMenu,
  showNotice,
  doUndo,
  doRedo,
  groupSelection,
  selectAll,
})

const menuSections = computed<MenuSection[]>(() => {
  const current = menu.value
  if (!current) return []
  if (current.kind === 'bar') return barSections.value
  if (current.kind === 'add') {
    // 跟右键里那个二级菜单是**同一份**内容，只是这里当一级用
    return addSections.value
  }
  if (current.kind === 'asset') {
    return assetSections.value
  }
  if (current.kind === 'link') {
    return current.link ? linkCandidateSections(current.link) : []
  }
  if (current.kind === 'editor') {
    /* 只有一件事：换这个窗口显示什么。扁平列表，一眼到底。 */
    const areaId = current.areaId
    const area = areaId ? findArea(layout.value.root, areaId) : null
    if (!area) return []
    /* ⚠️ 说的是**这个窗口现在显示什么**（`editorOf`），不是它"要"显示什么 */
    const shown = editorOf(area)
    return [
      {
        id: 'editor-list',
        title: `${EDITOR_META[shown].label}（${area.id}）`,
        titleStyle: 'divider' as const,
        items: EDITOR_ORDER.map((editor) => ({
          id: `editor-${editor}`,
          label: EDITOR_META[editor].label + (EDITOR_META[editor].implemented ? '' : '（未实现）'),
          icon: EDITOR_META[editor].icon,
          checked: shown === editor,
          disabled: !EDITOR_META[editor].implemented,
          run: () => ctx.setEditorOf(area.id, editor),
        })),
      },
    ]
  }
  if (current.kind === 'area') {
    /*
     * ★ Area 那套菜单**已向下拆到** `menus/areaSections.ts`：
     * 实测它只用得到本文件 67 个字段里的 6 个，和另外两套菜单**交集为 0** ——
     * 所以按耦合拆出去，而不是继续留在这个上千行的函数里。
     */
    return current.areaId
      ? areaSections(
          {
            layout: () => layout.value,
            swapAreas,
            toggleRegionOf,
            splitAreaOf,
            toggleMaximize,
            closeArea,
            setPanelOfSide: (id, side, tab) => ctx.setPanelOfSide(id, side, tab),
          },
          current.areaId,
        )
      : []
  }
  return contextSections.value
})

/**
 * 区分「选了某一项」和「直接关掉」—— 后者在 link 菜单里等于「断开」。
 *
 * ⚠️ `menuPicked` 由宿主注入（是个 ref），本文件**不能自己再声明一个** ——
 * 宿主更早的 `runGestureCommands` 也写它，两个副本会让「直接关掉 = 断开」静默失效。
 */
function onMenuSelect(item: { run?: () => void }) {
  ctx.menuPicked.value = true
  item.run?.()
}

function closeMenu() {
  const current = menu.value
  const picked = ctx.menuPicked.value
  ctx.menuPicked.value = false
  menu.value = null

  /*
   * link 菜单被直接关掉 = 用户不打算接任何东西。
   * 这时「从输入拖出来的那根线」的语义就是**断开** ——
   * 所以补上提示和撤销，跟以前的落空行为保持一致。
   */
  const undoEdge = current?.link?.undoEdge
  if (!picked && current?.kind === 'link' && undoEdge) {
    showNotice(
      `已断开：${undoEdge.source}.${undoEdge.sourceHandle} → ${undoEdge.target}.${undoEdge.targetHandle}`,
      { label: '撤销', run: () => putEdgeBack(undoEdge) },
    )
  }
}

/**
 * 事件坐标。
 *
 * ⚠️ Vue Flow 的 `event` 是 `MouseTouchEvent`（MouseEvent | TouchEvent），
 * 不是单纯的 MouseEvent —— 直接读 `.clientX` 类型过不了，
 * 触摸设备上运行时也真的是 undefined。
 */
function eventPoint(ev: MouseEvent | TouchEvent): { x: number; y: number } {
  if ('clientX' in ev) return { x: ev.clientX, y: ev.clientY }
  const touch = ev.changedTouches?.[0] ?? ev.touches?.[0]
  return { x: touch?.clientX ?? 0, y: touch?.clientY ?? 0 }
}

/** 某条连线当前是否静音 */
function edgeMuted(edgeId: string): boolean {
  const edge = (edgeViews() as unknown as { id?: string; muted?: boolean }[]).find(
    (e) => e.id === edgeId,
  )
  return Boolean(edge?.muted)
}

/*
 * ⚠️ Vue Flow 有**四个**右键入口，每一个都只是把事件转发出来、
 * **自己不 preventDefault**。漏掉任何一个，那条路径上就会同时弹出
 * 浏览器默认菜单：
 *
 *   nodeContextMenu       —— 右键节点
 *   paneContextMenu       —— 右键空白画布
 *   selectionContextMenu  —— 右键**多选产生的选择框**（只在多选时存在）
 *   edgeContextMenu       —— 右键连线
 *
 * 前两个一开始就接了；后两个是漏的 —— 表现就是「多选后右键还是会弹系统菜单」。
 */

function onNodeMenu(params: { event: MouseEvent | TouchEvent; node: { id: string } }) {
  params.event.preventDefault()
  ctx.menuPicked.value = false
  contextEdgeId.value = null

  // 右键一个没选中的节点 → 先把它选上，否则「排列」会作用在别处
  if (!selectedIdSet.value.has(params.node.id)) {
    flowStore.value?.addSelectedNodes?.([params.node])
  }
  menu.value = { kind: 'context', ...eventPoint(params.event) }
}

/**
 * 把屏幕上的一个点投影到某条线条上，返回画布坐标。
 *
 * 双击线条时，接通节点应该落在**线上的那个位置**，而不是双击的精确像素 ——
 * 精确像素可能离线条十几个像素远，放上去看着像没接上。
 */
function projectOntoEdge(edgeId: string, ev: MouseEvent | TouchEvent): Point | null {
  const host = graphRef.value
  if (!host) return null

  /* ⚠️ 选择器从 `graphDom.ts` 取（引擎的 DOM 契约） */
  const path = host.querySelector(
    `.${FLOW_DOM.edge}[data-id="${edgeId}"] .${FLOW_DOM.edgePath}`,
  ) as SVGPathElement | null
  if (!path) return null

  const samples = sampleEdgePathFine(path)
  if (samples.length === 0) return null

  const target = toFlowPoint(eventPoint(ev))
  let best = samples[0] as Point
  let bestDistance = Infinity
  for (const point of samples) {
    const distance = (point.x - target.x) ** 2 + (point.y - target.y) ** 2
    if (distance < bestDistance) {
      bestDistance = distance
      best = point
    }
  }
  return best
}

  return {
    contextSections,
    barSections,
    MENUS,
    barMenuId,
    openBarMenu,
    menuSections,
    onMenuSelect,
    closeMenu,
    eventPoint,
    edgeMuted,
    onNodeMenu,
    projectOntoEdge,
  }
}
